import { apiBaseUrl } from '@/platform/config'

// On native (Capacitor) the WebView origin is `https://localhost`, so a bare
// `/api/...` path resolves against the WebView itself and fails. `apiBaseUrl()`
// returns `VITE_API_BASE_URL` on native and `''` on web (same-origin Rails).
const BASE = `${apiBaseUrl()}/api/v1`

function headers(token: string) {
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  }
}

async function request(token: string, url: string, options: RequestInit = {}) {
  const res = await fetch(url, { headers: headers(token), ...options })
  if (!res.ok) {
    const text = await res.text()
    let message = `Request failed (${res.status})`
    try {
      const json = JSON.parse(text)
      if (json.error) message = json.error
    } catch {
      // Response wasn't JSON
    }
    throw new Error(message)
  }
  const json = await res.json()
  if (!json.success) throw new Error(json.error || 'Request failed')
  return json.data
}

// Inbox
export const fetchInbox = (token: string) =>
  request(token, `${BASE}/inbox`)

// Notifications
export const fetchNotifications = (token: string) =>
  request(token, `${BASE}/notifications`)

export const markNotificationRead = (token: string, id: string) =>
  request(token, `${BASE}/notifications/${id}/mark_read`, { method: 'PATCH' })

export const markAllNotificationsRead = (token: string) =>
  request(token, `${BASE}/notifications/mark_all_read`, { method: 'POST' })

export const deleteNotification = (token: string, id: string) =>
  request(token, `${BASE}/notifications/${id}`, { method: 'DELETE' })

// Conversations
export const fetchConversations = (token: string) =>
  request(token, `${BASE}/conversations`)

export const fetchConversation = (token: string, id: string) =>
  request(token, `${BASE}/conversations/${id}`)

export const createConversation = (token: string, data: Record<string, unknown>) =>
  request(token, `${BASE}/conversations`, {
    method: 'POST',
    body: JSON.stringify({ conversation: data }),
  })

// M-E4 — messageable contacts (assoc/circle peers) for the "New message" picker.
export const fetchContacts = (token: string, query = '') =>
  request(token, `${BASE}/conversations/contacts${query ? `?query=${encodeURIComponent(query)}` : ''}`)

// Conversation Messages
export const fetchMessages = (token: string, conversationId: string) =>
  request(token, `${BASE}/conversations/${conversationId}/messages`)

export const sendMessage = (token: string, conversationId: string, data: Record<string, unknown>) =>
  request(token, `${BASE}/conversations/${conversationId}/messages`, {
    method: 'POST',
    body: JSON.stringify({ message: data }),
  })

// Announcements
export const fetchAnnouncements = (token: string, associationId: string) =>
  request(token, `${BASE}/associations/${associationId}/announcements`)

export const createAnnouncement = (token: string, associationId: string, data: Record<string, unknown>) =>
  request(token, `${BASE}/associations/${associationId}/announcements`, {
    method: 'POST',
    body: JSON.stringify({ announcement: data }),
  })

export const updateAnnouncement = (token: string, associationId: string, id: string, data: Record<string, unknown>) =>
  request(token, `${BASE}/associations/${associationId}/announcements/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ announcement: data }),
  })

export const acknowledgeAnnouncement = (token: string, associationId: string, id: string) =>
  request(token, `${BASE}/associations/${associationId}/announcements/${id}/acknowledge`, {
    method: 'POST',
  })

// Events
export const fetchMyEvents = (token: string) =>
  request(token, `${BASE}/community/events`)

export const fetchEvents = (token: string, associationId: string) =>
  request(token, `${BASE}/associations/${associationId}/events`)

export const fetchEvent = (token: string, associationId: string, id: string) =>
  request(token, `${BASE}/associations/${associationId}/events/${id}`)

export const createEvent = (token: string, associationId: string, data: Record<string, unknown>) =>
  request(token, `${BASE}/associations/${associationId}/events`, {
    method: 'POST',
    body: JSON.stringify({ event: data }),
  })

export const updateEvent = (token: string, associationId: string, id: string, data: Record<string, unknown>) =>
  request(token, `${BASE}/associations/${associationId}/events/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ event: data }),
  })

export const cancelEvent = (token: string, associationId: string, id: string) =>
  request(token, `${BASE}/associations/${associationId}/events/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ event: { status: 'cancelled' } }),
  })

export const deleteEvent = (token: string, associationId: string, id: string) =>
  request(token, `${BASE}/associations/${associationId}/events/${id}`, {
    method: 'DELETE',
  })

export const duplicateEvent = (token: string, associationId: string, id: string) =>
  request(token, `${BASE}/associations/${associationId}/events/${id}/duplicate`, {
    method: 'POST',
  })

// Event Registrations
export const fetchRegistrations = (token: string, associationId: string, eventId: string) =>
  request(token, `${BASE}/associations/${associationId}/events/${eventId}/registrations`)

export const createRegistration = (token: string, associationId: string, eventId: string, data: Record<string, unknown>) =>
  request(token, `${BASE}/associations/${associationId}/events/${eventId}/registrations`, {
    method: 'POST',
    body: JSON.stringify({ registration: data }),
  })

export const updateRegistration = (token: string, associationId: string, eventId: string, id: string, data: Record<string, unknown>) =>
  request(token, `${BASE}/associations/${associationId}/events/${eventId}/registrations/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ registration: data }),
  })

export const deleteRegistration = (token: string, associationId: string, eventId: string, id: string) =>
  request(token, `${BASE}/associations/${associationId}/events/${eventId}/registrations/${id}`, {
    method: 'DELETE',
  })

// Notification Preferences
export const fetchNotificationPreferences = (token: string) =>
  request(token, `${BASE}/notification_preferences`)

export const updateNotificationPreference = (token: string, id: string, data: Record<string, unknown>) =>
  request(token, `${BASE}/notification_preferences/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ preference: data }),
  })

export const saveAllPreferences = (token: string, preferences: Record<string, unknown>[]) =>
  request(token, `${BASE}/notification_preferences/save_all`, {
    method: 'POST',
    body: JSON.stringify({ preferences }),
  })

// Message Templates
export const fetchTemplates = (token: string, associationId: string) =>
  request(token, `${BASE}/associations/${associationId}/message_templates`)

export const createTemplate = (token: string, associationId: string, data: Record<string, unknown>) =>
  request(token, `${BASE}/associations/${associationId}/message_templates`, {
    method: 'POST',
    body: JSON.stringify({ template: data }),
  })

export const updateTemplate = (token: string, associationId: string, id: string, data: Record<string, unknown>) =>
  request(token, `${BASE}/associations/${associationId}/message_templates/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ template: data }),
  })

export const deleteTemplate = (token: string, associationId: string, id: string) =>
  request(token, `${BASE}/associations/${associationId}/message_templates/${id}`, {
    method: 'DELETE',
  })

// Quick Replies
export const fetchQuickReplies = (token: string) =>
  request(token, `${BASE}/quick_replies`)

export const createQuickReply = (token: string, data: Record<string, unknown>) =>
  request(token, `${BASE}/quick_replies`, {
    method: 'POST',
    body: JSON.stringify({ quick_reply: data }),
  })

export const deleteQuickReply = (token: string, id: string) =>
  request(token, `${BASE}/quick_replies/${id}`, {
    method: 'DELETE',
  })

// Consolidated Calendar (M-EVT-US050)
export const fetchAllMyEvents = (token: string) =>
  request(token, `${BASE}/my_events`)

// Attendance Certificate (M-EVT-US052)
export const downloadCertificate = (token: string, associationId: string, eventId: string) =>
  fetch(`${BASE}/associations/${associationId}/events/${eventId}/attendances/certificate`, {
    headers: { 'Authorization': `Bearer ${token}` },
  }).then((res) => {
    if (!res.ok) throw new Error('Failed to download certificate')
    return res.blob()
  })

// Event Surveys (M-EVT-US053)
export const fetchEventSurvey = (token: string, associationId: string, eventId: string, surveyId: string) =>
  request(token, `${BASE}/associations/${associationId}/events/${eventId}/event_surveys/${surveyId}`)

export const createEventSurvey = (token: string, associationId: string, eventId: string, data: Record<string, unknown>) =>
  request(token, `${BASE}/associations/${associationId}/events/${eventId}/event_surveys`, {
    method: 'POST',
    body: JSON.stringify({ event_survey: data }),
  })

export const sendEventSurvey = (token: string, associationId: string, eventId: string, surveyId: string) =>
  request(token, `${BASE}/associations/${associationId}/events/${eventId}/event_surveys/${surveyId}/send_survey`, {
    method: 'POST',
  })

export const closeEventSurvey = (token: string, associationId: string, eventId: string, surveyId: string) =>
  request(token, `${BASE}/associations/${associationId}/events/${eventId}/event_surveys/${surveyId}/close`, {
    method: 'POST',
  })

export const fetchSurveyResponses = (token: string, associationId: string, eventId: string, surveyId: string) =>
  request(token, `${BASE}/associations/${associationId}/events/${eventId}/event_surveys/${surveyId}/responses`)

// Event Media (M-EVT-US054)
export const fetchEventMedia = (token: string, associationId: string, eventId: string) =>
  request(token, `${BASE}/associations/${associationId}/events/${eventId}/event_media`)

export const uploadEventMedia = async (token: string, associationId: string, eventId: string, file: File, mediaType: string, caption?: string) => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('media_type', mediaType)
  if (caption) formData.append('caption', caption)

  const res = await fetch(`${BASE}/associations/${associationId}/events/${eventId}/event_media`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData,
  })
  if (!res.ok) throw new Error('Upload failed')
  const json = await res.json()
  if (!json.success) throw new Error(json.error || 'Upload failed')
  return json.data
}

export const deleteEventMedia = (token: string, associationId: string, eventId: string, id: string) =>
  request(token, `${BASE}/associations/${associationId}/events/${eventId}/event_media/${id}`, {
    method: 'DELETE',
  })

// Event Translations (M-EVT-US055)
export const fetchEventTranslations = (token: string, associationId: string, eventId: string) =>
  request(token, `${BASE}/associations/${associationId}/events/${eventId}/event_translations`)

export const createEventTranslation = (token: string, associationId: string, eventId: string, data: Record<string, unknown>) =>
  request(token, `${BASE}/associations/${associationId}/events/${eventId}/event_translations`, {
    method: 'POST',
    body: JSON.stringify({ event_translation: data }),
  })

export const updateEventTranslation = (token: string, associationId: string, eventId: string, id: string, data: Record<string, unknown>) =>
  request(token, `${BASE}/associations/${associationId}/events/${eventId}/event_translations/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ event_translation: data }),
  })

export const deleteEventTranslation = (token: string, associationId: string, eventId: string, id: string) =>
  request(token, `${BASE}/associations/${associationId}/events/${eventId}/event_translations/${id}`, {
    method: 'DELETE',
  })

// Vote Recording (M-EVT-US048)
export const recordVote = (
  token: string,
  associationId: string,
  eventId: string,
  agendaItemId: string,
  voteResult: Record<string, unknown>
) =>
  request(
    token,
    `${BASE}/associations/${associationId}/events/${eventId}/agenda/items/${agendaItemId}/record_vote`,
    {
      method: 'PATCH',
      body: JSON.stringify({ vote_result: voteResult }),
    }
  )
